import { m, useReducedMotionConfig } from "framer-motion";
import { type CSSProperties, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  Tooltip,
  type MouseHandlerDataParam,
  XAxis,
  YAxis,
} from "recharts";
import { AnimatedNumberText } from "~/components/animated-number";
import { Button } from "~/components/ui/button";
import { ChartContainer, type ChartConfig } from "~/components/ui/chart";
import { fluidSprings } from "~/components/ui/fluid-motion";
import {
  ONBOARDING_V1_PROJECTION_ANNUAL_RATES,
  createOnboardingV1Projection,
  type OnboardingV1ProjectionPoint,
} from "~/features/start/data/onboarding-v1-projection";
import { formatPercent } from "~/lib/display-format";
import { formatLanguageNumber } from "~/lib/language-lens/intl";
import { m as messages } from "~/lib/i18n";
import {
  formatMarketCompactCurrency,
  formatMarketCurrency,
} from "~/lib/value-format";

interface OnboardingV1ProjectionStepProps {
  readonly onContinue: () => void;
}

type ProjectionSeriesId = "glider" | "highYieldSavings" | "otherInvestingApps";

type ProjectionRevealPhase =
  | "title"
  | "principal"
  | "chart"
  | "lines"
  | "results";

const PROJECTION_REVEAL_PHASES: readonly ProjectionRevealPhase[] = [
  "title",
  "principal",
  "chart",
  "lines",
  "results",
];

const TYPE_ON_EASE = [0.22, 1, 0.36, 1] as const;
const TYPE_ON_CLIP_HIDDEN = "inset(-0.18em 100% -0.24em 0)";
const TYPE_ON_CLIP_VISIBLE = "inset(-0.18em 0% -0.24em 0)";
const TITLE_TYPE_ON_SECONDS = 0.92;
const PRINCIPAL_TYPE_ON_SECONDS = 0.68;
const PROJECTION_X_TICKS = [0, 2, 4, 6, 8, 10] as const;
const PROJECTION_Y_TICKS = [
  10_000, 15_000, 20_000, 25_000, 30_000, 35_000,
] as const;
const PROJECTION_Y_MIN = 10_000;
const PROJECTION_Y_MAX = 35_000;
const PROJECTION_CHART_HEIGHT_PX = 360;
const PROJECTION_CHART_MARGIN_TOP_PX = 8;
const PROJECTION_X_AXIS_HEIGHT_PX = 48;
const AXIS_TICK_STAGGER_SECONDS = fluidSprings.fast.duration / 2;

interface ProjectionResultStyle extends CSSProperties {
  readonly "--onboarding-v1-projection-result-top": string;
}

// This long draw is the visual story of the chart, so it intentionally uses
// chart-specific timing instead of the short interaction spring tiers.
const LINE_DRAW_TIMING = {
  glider: { beginMs: 400, durationMs: 3_000 },
  highYieldSavings: { beginMs: 0, durationMs: 3_400 },
  otherInvestingApps: { beginMs: 100, durationMs: 3_300 },
} as const satisfies Record<
  ProjectionSeriesId,
  { readonly beginMs: number; readonly durationMs: number }
>;

const REVEAL_PHASE_DURATION_MS = {
  chart: Math.round(
    (fluidSprings.slow.duration + fluidSprings.moderate.duration) * 1_000,
  ),
  lines: Math.max(
    ...Object.values(LINE_DRAW_TIMING).map(
      ({ beginMs, durationMs }) => beginMs + durationMs,
    ),
  ),
  principal: Math.round(PRINCIPAL_TYPE_ON_SECONDS * 1_000),
  title: Math.round(TITLE_TYPE_ON_SECONDS * 1_000),
} as const satisfies Record<Exclude<ProjectionRevealPhase, "results">, number>;

const PROJECTION_ENDPOINT_FILLS = {
  glider: "var(--onboarding-v1-projection-glider)",
  highYieldSavings: "#777",
  otherInvestingApps: "#b8b8b8",
} as const satisfies Record<ProjectionSeriesId, string>;

function hasReachedRevealPhase(
  current: ProjectionRevealPhase,
  target: ProjectionRevealPhase,
): boolean {
  return (
    PROJECTION_REVEAL_PHASES.indexOf(current) >=
    PROJECTION_REVEAL_PHASES.indexOf(target)
  );
}

function getNextRevealPhase(
  current: ProjectionRevealPhase,
): ProjectionRevealPhase | null {
  const next = PROJECTION_REVEAL_PHASES[
    PROJECTION_REVEAL_PHASES.indexOf(current) + 1
  ] as ProjectionRevealPhase | undefined;
  return next ?? null;
}

function formatUsd(value: number, fractionDigits: number): string {
  return formatMarketCurrency(value, {
    currency: "USD",
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  });
}

function getSeriesLabel(id: ProjectionSeriesId): string {
  switch (id) {
    case "glider":
      return messages.onboarding_v1_projection_glider_label();
    case "highYieldSavings":
      return messages.onboarding_v1_projection_savings_label();
    case "otherInvestingApps":
      return messages.onboarding_v1_projection_other_apps_label();
  }
}

function formatYearTick(year: number): string {
  if (year === 0) {
    return messages.onboarding_v1_projection_year_zero();
  }
  if (year === 10) {
    return messages.onboarding_v1_projection_years({
      years: formatLanguageNumber(year),
    });
  }
  return formatLanguageNumber(year);
}

function getAxisTickRevealIndex(
  value: number,
  ticks: readonly number[],
): number {
  return Math.max(
    ticks.findIndex((candidate) => candidate === value),
    0,
  );
}

function getProjectionResultStyle(value: number): ProjectionResultStyle {
  const plotHeight =
    PROJECTION_CHART_HEIGHT_PX -
    PROJECTION_CHART_MARGIN_TOP_PX -
    PROJECTION_X_AXIS_HEIGHT_PX;
  const y =
    PROJECTION_CHART_MARGIN_TOP_PX +
    ((PROJECTION_Y_MAX - value) / (PROJECTION_Y_MAX - PROJECTION_Y_MIN)) *
      plotHeight;

  return {
    "--onboarding-v1-projection-result-top": `${
      (y / PROJECTION_CHART_HEIGHT_PX) * 100
    }%`,
  };
}

function getHoveredProjectionYear(
  activeLabel: MouseHandlerDataParam["activeLabel"],
): number | null {
  const year =
    typeof activeLabel === "number"
      ? activeLabel
      : typeof activeLabel === "string" && activeLabel.trim() !== ""
        ? Number(activeLabel)
        : Number.NaN;

  return Number.isInteger(year) && year >= 0 ? year : null;
}

function ProjectionHoverTooltip() {
  return null;
}

interface ProjectionYearTickProps {
  readonly payload?: { readonly value?: unknown };
  readonly reduceMotion: boolean;
  readonly visible: boolean;
  readonly x?: number | string;
  readonly y?: number | string;
}

/**
 * Figma anchors the edge ticks into the plot. Recharts centers every tick by
 * default, which clips "10 years" and lets "Year 0" crowd the y-axis labels.
 */
function ProjectionYearTick(props: ProjectionYearTickProps) {
  const year = Number(props.payload?.value);
  const x = Number(props.x);
  const y = Number(props.y);
  if (!Number.isFinite(year) || !Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }

  return (
    <m.text
      animate={
        props.visible
          ? { filter: "blur(0px)", opacity: 1 }
          : { filter: "blur(2px)", opacity: 0 }
      }
      className="onboarding-v1__projection-axis-tick"
      data-year={year}
      dy={8}
      fill="#fff"
      initial={false}
      textAnchor={year === 0 ? "start" : year === 10 ? "end" : "middle"}
      transition={
        props.reduceMotion
          ? { duration: 0 }
          : {
              ...fluidSprings.moderate,
              delay:
                getAxisTickRevealIndex(year, PROJECTION_X_TICKS) *
                AXIS_TICK_STAGGER_SECONDS,
            }
      }
      x={x}
      y={y}
    >
      {formatYearTick(year)}
    </m.text>
  );
}

interface ProjectionValueTickProps {
  readonly payload?: { readonly value?: unknown };
  readonly reduceMotion: boolean;
  readonly visible: boolean;
  readonly x?: number | string;
  readonly y?: number | string;
}

function ProjectionValueTick(props: ProjectionValueTickProps) {
  const value = Number(props.payload?.value);
  const x = Number(props.x);
  const y = Number(props.y);
  if (!Number.isFinite(value) || !Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }

  const animate = props.visible
    ? { filter: "blur(0px)", opacity: 1 }
    : { filter: "blur(2px)", opacity: 0 };
  const transition = props.reduceMotion
    ? { duration: 0 }
    : {
        ...fluidSprings.moderate,
        delay:
          getAxisTickRevealIndex(value, PROJECTION_Y_TICKS) *
          AXIS_TICK_STAGGER_SECONDS,
      };
  const sharedProps = {
    animate,
    dy: 4,
    fill: "#fff",
    initial: false,
    textAnchor: "end" as const,
    transition,
    x,
    y,
  };

  return (
    <>
      <m.text
        {...sharedProps}
        className="onboarding-v1__projection-axis-tick onboarding-v1__projection-axis-tick--full"
      >
        {formatUsd(value, 0)}
      </m.text>
      <m.text
        {...sharedProps}
        className="onboarding-v1__projection-axis-tick onboarding-v1__projection-axis-tick--compact"
      >
        {formatMarketCompactCurrency(value, {
          currency: "USD",
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        })}
      </m.text>
    </>
  );
}

interface ProjectionSeriesItem {
  readonly annualRate: number;
  readonly endpointValue: number;
  readonly id: ProjectionSeriesId;
  readonly value: number;
}

function createProjectionSeries(
  displayedPoint: OnboardingV1ProjectionPoint,
  endpoint: OnboardingV1ProjectionPoint,
) {
  return [
    {
      annualRate: ONBOARDING_V1_PROJECTION_ANNUAL_RATES.glider,
      endpointValue: endpoint.glider,
      id: "glider",
      value: displayedPoint.glider,
    },
    {
      annualRate: ONBOARDING_V1_PROJECTION_ANNUAL_RATES.otherInvestingApps,
      endpointValue: endpoint.otherInvestingApps,
      id: "otherInvestingApps",
      value: displayedPoint.otherInvestingApps,
    },
    {
      annualRate: ONBOARDING_V1_PROJECTION_ANNUAL_RATES.highYieldSavings,
      endpointValue: endpoint.highYieldSavings,
      id: "highYieldSavings",
      value: displayedPoint.highYieldSavings,
    },
  ] as const satisfies readonly ProjectionSeriesItem[];
}

function createProjectionChartConfig(): ChartConfig {
  return {
    glider: {
      color: "var(--onboarding-v1-projection-glider)",
      label: getSeriesLabel("glider"),
    },
    highYieldSavings: {
      color: "var(--onboarding-v1-projection-highYieldSavings)",
      label: getSeriesLabel("highYieldSavings"),
    },
    otherInvestingApps: {
      color: "var(--onboarding-v1-projection-otherInvestingApps)",
      label: getSeriesLabel("otherInvestingApps"),
    },
  };
}

interface ProjectionHeadingProps {
  readonly principalUsd: number;
  readonly reduceMotion: boolean;
  readonly showPrincipal: boolean;
  readonly titleIsVisible: boolean;
}

function ProjectionHeading(props: ProjectionHeadingProps) {
  return (
    <div className="onboarding-v1__projection-heading">
      <m.h1
        animate={
          props.titleIsVisible
            ? {
                clipPath: TYPE_ON_CLIP_VISIBLE,
                filter: "blur(0px)",
                opacity: 1,
              }
            : {
                clipPath: TYPE_ON_CLIP_HIDDEN,
                filter: "blur(2px)",
                opacity: 0.45,
              }
        }
        className="onboarding-v1__projection-type-on"
        initial={false}
        transition={
          props.reduceMotion
            ? { duration: 0 }
            : { duration: TITLE_TYPE_ON_SECONDS, ease: TYPE_ON_EASE }
        }
      >
        {messages.onboarding_v1_projection_heading()}
      </m.h1>
      <m.p
        animate={
          props.showPrincipal
            ? {
                clipPath: TYPE_ON_CLIP_VISIBLE,
                filter: "blur(0px)",
                opacity: 1,
              }
            : {
                clipPath: TYPE_ON_CLIP_HIDDEN,
                filter: "blur(2px)",
                opacity: 0,
              }
        }
        className="onboarding-v1__projection-type-on"
        initial={false}
        transition={
          props.reduceMotion
            ? { duration: 0 }
            : { duration: PRINCIPAL_TYPE_ON_SECONDS, ease: TYPE_ON_EASE }
        }
      >
        {messages.onboarding_v1_projection_starting_with({
          principal: formatUsd(props.principalUsd, 0),
        })}
      </m.p>
    </div>
  );
}

interface ProjectionChartProps {
  readonly chartSeries: readonly ProjectionSeriesItem[];
  readonly clearHoveredPoint: () => void;
  readonly handleChartPointerMove: (state: MouseHandlerDataParam) => void;
  readonly points: readonly OnboardingV1ProjectionPoint[];
  readonly reduceMotion: boolean;
  readonly showChart: boolean;
  readonly showLines: boolean;
  readonly showResults: boolean;
}

function ProjectionChart(props: ProjectionChartProps) {
  return (
    <m.div
      animate={
        props.showChart
          ? { opacity: 1, scaleY: 1, y: 0 }
          : { opacity: 0, scaleY: 0.94, y: 10 }
      }
      className="onboarding-v1__projection-chart-frame"
      initial={false}
      transition={props.reduceMotion ? { duration: 0 } : fluidSprings.slow}
    >
      <ChartContainer
        aria-label={messages.onboarding_v1_projection_chart_label()}
        className="onboarding-v1__projection-chart"
        config={createProjectionChartConfig()}
        initialDimension={{ height: 360, width: 820 }}
        role="img"
      >
        <LineChart
          accessibilityLayer={false}
          data={props.points}
          margin={{ bottom: 0, left: 0, right: 14, top: 8 }}
          onMouseLeave={props.clearHoveredPoint}
          onMouseMove={props.handleChartPointerMove}
          onTouchEnd={props.clearHoveredPoint}
          onTouchMove={props.handleChartPointerMove}
        >
          <CartesianGrid
            horizontal
            strokeDasharray="2 5"
            syncWithTicks
            vertical={false}
          />
          <XAxis
            axisLine={false}
            dataKey="year"
            height={48}
            interval={0}
            padding={{ left: 0, right: 0 }}
            tick={
              <ProjectionYearTick
                reduceMotion={props.reduceMotion}
                visible={props.showChart}
              />
            }
            tickLine={false}
            tickMargin={12}
            ticks={PROJECTION_X_TICKS}
          />
          <YAxis
            axisLine={false}
            domain={[PROJECTION_Y_MIN, PROJECTION_Y_MAX]}
            tick={
              <ProjectionValueTick
                reduceMotion={props.reduceMotion}
                visible={props.showChart}
              />
            }
            tickLine={false}
            tickMargin={10}
            ticks={PROJECTION_Y_TICKS}
            width={68}
          />
          {props.showResults ? (
            <Tooltip
              content={<ProjectionHoverTooltip />}
              cursor={{
                stroke: "rgb(255 255 255 / 0.42)",
                strokeDasharray: "3 5",
                strokeWidth: 1,
              }}
              isAnimationActive={false}
            />
          ) : null}
          {props.showLines
            ? props.chartSeries.map((item) => {
                const timing = LINE_DRAW_TIMING[item.id];
                return (
                  <Line
                    activeDot={
                      props.showResults
                        ? {
                            fill: PROJECTION_ENDPOINT_FILLS[item.id],
                            r: item.id === "glider" ? 6 : 5,
                            stroke: "#050505",
                            strokeWidth: 2,
                          }
                        : false
                    }
                    animationBegin={timing.beginMs}
                    animationDuration={timing.durationMs}
                    animationEasing="ease-in-out"
                    className={`onboarding-v1__projection-line onboarding-v1__projection-line--${item.id}`}
                    dataKey={item.id}
                    dot={false}
                    isAnimationActive={!props.reduceMotion}
                    key={item.id}
                    stroke={`var(--onboarding-v1-projection-${item.id})`}
                    strokeWidth={item.id === "glider" ? 3.25 : 1.5}
                    type="monotone"
                  />
                );
              })
            : null}
          {props.showResults
            ? props.chartSeries.map((item) => (
                <ReferenceDot
                  className={`onboarding-v1__projection-endpoint onboarding-v1__projection-endpoint--${item.id}`}
                  fill={PROJECTION_ENDPOINT_FILLS[item.id]}
                  key={item.id}
                  r={item.id === "glider" ? 7.5 : 6.5}
                  stroke={PROJECTION_ENDPOINT_FILLS[item.id]}
                  strokeWidth={2.5}
                  x={10}
                  y={item.endpointValue}
                />
              ))
            : null}
        </LineChart>
      </ChartContainer>
    </m.div>
  );
}

interface ProjectionResultsProps {
  readonly displayedYear: number;
  readonly hoveredPoint: OnboardingV1ProjectionPoint | null;
  readonly reduceMotion: boolean;
  readonly series: readonly ProjectionSeriesItem[];
  readonly showResults: boolean;
  readonly years: number;
}

function ProjectionResults(props: ProjectionResultsProps) {
  return (
    <ul
      aria-label={messages.onboarding_v1_projection_results_label({
        years: formatLanguageNumber(props.years),
      })}
      className="onboarding-v1__projection-results"
      data-displayed-year={props.displayedYear}
      data-visible={props.showResults}
    >
      {props.series.map((item, index) => (
        <m.li
          animate={{ opacity: props.showResults ? 1 : 0 }}
          className="onboarding-v1__projection-result"
          data-series={item.id}
          initial={false}
          key={item.id}
          style={getProjectionResultStyle(item.endpointValue)}
          transition={
            props.reduceMotion
              ? { duration: 0 }
              : { ...fluidSprings.moderate, delay: index * 0.1 }
          }
        >
          <span className="onboarding-v1__projection-result-label">
            {getSeriesLabel(item.id)}
          </span>
          <span className="onboarding-v1__projection-result-copy">
            <strong>
              <AnimatedNumberText
                motion={
                  props.reduceMotion || props.hoveredPoint !== null
                    ? "static"
                    : "animated"
                }
                text={formatUsd(item.value, 2)}
              />
            </strong>
            <span className="onboarding-v1__projection-result-rate">
              {messages.onboarding_v1_projection_average_return({
                rate: formatPercent(item.annualRate * 100, {
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0,
                }),
              })}
            </span>
          </span>
        </m.li>
      ))}
    </ul>
  );
}

export function OnboardingV1ProjectionStep(
  props: OnboardingV1ProjectionStepProps,
) {
  const reduceMotion = useReducedMotionConfig() ?? false;
  const [revealPhase, setRevealPhase] = useState<ProjectionRevealPhase>(() =>
    reduceMotion ? "results" : "title",
  );
  const [titleIsVisible, setTitleIsVisible] = useState(reduceMotion);
  const [hoveredPoint, setHoveredPoint] =
    useState<OnboardingV1ProjectionPoint | null>(null);
  useEffect(() => {
    if (reduceMotion) {
      setTitleIsVisible(true);
      setRevealPhase("results");
      return;
    }

    // The parent AnimatePresence disables descendant initial animations on a
    // hard refresh. Triggering the title after mount makes this a real animate
    // state change instead of an initial animation, so refresh and navigation
    // follow the same visual sequence.
    const startTitleTimeout = globalThis.setTimeout(() => {
      setTitleIsVisible(true);
    }, 0);

    return () => {
      globalThis.clearTimeout(startTitleTimeout);
    };
  }, [reduceMotion]);
  useEffect(() => {
    if (reduceMotion || revealPhase === "results") {
      return;
    }

    const nextPhase = getNextRevealPhase(revealPhase);
    if (!nextPhase) {
      return;
    }

    // Phase progression is product behavior, so it cannot depend on an
    // animation completion callback that a browser may legitimately skip.
    const phaseTimeout = globalThis.setTimeout(() => {
      setRevealPhase((current) =>
        current === revealPhase ? nextPhase : current,
      );
    }, REVEAL_PHASE_DURATION_MS[revealPhase]);

    return () => {
      globalThis.clearTimeout(phaseTimeout);
    };
  }, [reduceMotion, revealPhase]);
  const projection = useMemo(() => createOnboardingV1Projection(), []);
  const endpoint = projection.points.at(-1);
  if (!endpoint) {
    return null;
  }

  const displayedPoint = hoveredPoint ?? endpoint;
  const series = createProjectionSeries(displayedPoint, endpoint);
  const chartSeries = [series[2], series[1], series[0]] as const;
  const showPrincipal = hasReachedRevealPhase(revealPhase, "principal");
  const showChart = hasReachedRevealPhase(revealPhase, "chart");
  const showLines = hasReachedRevealPhase(revealPhase, "lines");
  const showResults = hasReachedRevealPhase(revealPhase, "results");

  function handleChartPointerMove(state: MouseHandlerDataParam) {
    if (!showResults || !state.isTooltipActive) {
      return;
    }

    const year = getHoveredProjectionYear(state.activeLabel);
    const point =
      year === null
        ? undefined
        : projection.points.find((candidate) => candidate.year === year);
    if (point) {
      setHoveredPoint((current) =>
        current?.year === point.year ? current : point,
      );
    }
  }

  function clearHoveredPoint() {
    setHoveredPoint(null);
  }

  return (
    <div
      className="onboarding-v1__projection-step onboarding-v1__flow-step"
      data-reveal-phase={revealPhase}
      data-testid="onboarding-v1-step-projection"
    >
      <div className="onboarding-v1__flow-body">
        <ProjectionHeading
          principalUsd={projection.principalUsd}
          reduceMotion={reduceMotion}
          showPrincipal={showPrincipal}
          titleIsVisible={titleIsVisible}
        />

        <div className="onboarding-v1__projection-comparison">
          <ProjectionChart
            chartSeries={chartSeries}
            clearHoveredPoint={clearHoveredPoint}
            handleChartPointerMove={handleChartPointerMove}
            points={projection.points}
            reduceMotion={reduceMotion}
            showChart={showChart}
            showLines={showLines}
            showResults={showResults}
          />
          <ProjectionResults
            displayedYear={displayedPoint.year}
            hoveredPoint={hoveredPoint}
            reduceMotion={reduceMotion}
            series={series}
            showResults={showResults}
            years={projection.years}
          />
        </div>
      </div>

      <m.div
        animate={showResults ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
        className="onboarding-v1__flow-actions"
        initial={false}
        transition={reduceMotion ? { duration: 0 } : fluidSprings.moderate}
      >
        <Button
          className="onboarding-v1__projection-continue"
          disabled={!showResults}
          onClick={props.onContinue}
          size="lg"
          type="button"
          variant="inverse"
        >
          {messages.onboarding_v1_projection_continue_action()}
        </Button>
      </m.div>
    </div>
  );
}
